// template-ui/services/route-lowercase.service.ts
import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd, Event, UrlTree } from '@angular/router';
import { filter } from 'rxjs/operators';

/**
 * Service that automatically converts specified route parameters from uppercase to lowercase
 * during navigation. This is useful for maintaining consistent route casing while allowing
 * flexible input from data sources.
 *
 * @example
 * ```typescript
 * // In app initialization
 * routeLowercaseService.setRouteParamsToLowercase(['some_uppercase_code', 'another_code']);
 *
 * // When navigating to '/something/SOME_UPPERCASE_CODE/25'
 * // Automatically converts to '/something/some_uppercase_code/25'
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class RouteLowercaseService {
  private router = inject(Router);
  private routeParamsToLowercase: string[] = [];

  /**
   * Initializes the service and sets up router method overrides and navigation monitoring.
   * Automatically starts intercepting router navigation calls.
   */
  constructor() {
    // Listen for navigation events
    this.router.events
      .pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.handleNavigation(event.url);
      });

    // Override router methods
    this.overrideRouterMethods();
  }

  /**
   * Configures which route parameters should be automatically converted to lowercase.
   *
   * @param params Array of route parameter names that should be lowercased
   * @example
   * ```typescript
   * setRouteParamsToLowercase(['some_uppercase_code', 'another_code']);
   * // Now 'SOME_UPPERCASE_CODE' → 'some_uppercase_code', 'ANOTHER_CODE' → 'another_code', etc.
   * ```
   */
  setRouteParamsToLowercase(params: string[]): void {
    this.routeParamsToLowercase = params;
  }

  /**
   * Overrides the Angular Router's navigate and navigateByUrl methods to intercept
   * navigation calls and apply lowercase transformations before routing.
   *
   * @private
   */
  private overrideRouterMethods(): void {
    const originalNavigate = this.router.navigate.bind(this.router);
    this.router.navigate = (commands: any[], extras?: any) => {
      // console.log('🔍 Router.navigate intercepted - Original commands:', commands);
      const transformedCommands = this.transformRouteCommands(commands);
      // console.log('🔍 Router.navigate intercepted - Transformed commands:', transformedCommands);
      return originalNavigate(transformedCommands, extras);
    };

    const originalNavigateByUrl = this.router.navigateByUrl.bind(this.router);
    this.router.navigateByUrl = (url: string | UrlTree, extras?: any) => {
      // console.log('🔍 Router.navigateByUrl intercepted - Original URL:', url);

      if (typeof url === 'string') {
        url = this.transformUrlString(url);
        // console.log('🔍 Router.navigateByUrl intercepted - Transformed URL (string):', url);
      } else if (url instanceof UrlTree) {
        // Transform UrlTree by converting to string, transforming, then parsing back
        const urlString = this.router.serializeUrl(url);
        const transformedUrlString = this.transformUrlString(urlString);
        if (transformedUrlString !== urlString) {
          url = this.router.parseUrl(transformedUrlString);
          // console.log('🔍 Router.navigateByUrl intercepted - Transformed URL (UrlTree):', transformedUrlString);
        }
      }

      return originalNavigateByUrl(url, extras);
    };
  }

  /**
   * Handles completed navigation events to check if a redirect is needed
   * for URLs that contain uppercase route parameters.
   *
   * @param url The current URL after navigation
   * @private
   */
  private handleNavigation(url: string): void {
    // Check if URL contains uppercase route params that should be lowercase
    const shouldRedirect = this.routeParamsToLowercase.some(param =>
      url.includes(`/${param.toUpperCase()}/`)
    );

    if (shouldRedirect) {
      const correctedUrl = this.transformUrlString(url);
      if (correctedUrl !== url) {
        // console.log(`🔄 Redirecting: ${url} → ${correctedUrl}`);
        this.router.navigateByUrl(correctedUrl, { replaceUrl: true }).catch(err => console.error(`Failed to navigateByUrl: ${err}`));
      }
    }
  }

  /**
   * Transforms an array of route commands by lowercasing specified parameters.
   * Handles both individual route segments and full URL strings within the command array.
   *
   * @param commands Array of route commands from router.navigate()
   * @returns Transformed array with lowercase route parameters
   * @private
   */
  private transformRouteCommands(commands: any[]): any[] {
    return commands.map(command => {
      if (typeof command === 'string') {
        if (command.includes('/')) {
          return this.transformUrlString(command);
        } else {
          const lowerCommand = command.toLowerCase();
          if (this.routeParamsToLowercase.includes(lowerCommand)) {
            // console.log(`🔄 Transformed segment: ${command} → ${lowerCommand}`);
            return lowerCommand;
          }
        }
      }
      return command;
    });
  }

  /**
   * Transforms a URL string by converting specified uppercase route parameters to lowercase.
   * Preserves other parts of the URL (like CDC numbers) that should not be modified.
   *
   * @param url The URL string to transform
   * @returns URL string with specified route parameters converted to lowercase
   * @private
   */
  private transformUrlString(url: string): string {
    let transformedUrl = url;
    this.routeParamsToLowercase.forEach(param => {
      const upperParam = param.toUpperCase();
      transformedUrl = transformedUrl.replace(new RegExp(`/${upperParam}/`, 'g'), `/${param}/`);
      transformedUrl = transformedUrl.replace(new RegExp(`/${upperParam}$`), `/${param}`);
    });
    return transformedUrl;
  }
}